import { routeConstants } from "constants/routeConstants";
import { useImageSection } from "pages/Profile/HighLight/ImageSection/useImageSection";
import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { FiLogOut } from "react-icons/fi";
import { IoIosArrowForward } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "slice";
import { clearUserState } from "slice/user";
import { logoutUser, useLogoutMutation } from "slice/userAuth";
import { AuthState } from "slice/userAuth/types";
import { getNameInitials } from "utils/getNameInitials";
import * as Styled from "./Menu.style";
import { Props } from "./types";

export const Menu: FC<Props> = React.memo(
    ({ menuOpen, setMenuOpen, is_agency }) => {
        const buttonRef = useRef(null);
        const navRef = useRef(null);
        const toggleMenu = () => setMenuOpen(!menuOpen);
        const { pathname } = useLocation();

        const { first_name, last_name } = useAppSelector((state) => state.user),
            { access_token } = useAppSelector((state) => state.userAuth),
            [logout, { data }] = useLogoutMutation(),
            navigateTo = useNavigate(),
            dispatch = useAppDispatch();

        const { images, handleError } = useImageSection();

        const userProfileUrl = useMemo(() => {
            if (is_agency) {
                return routeConstants.agencyInformation;
            } else return routeConstants.profile.root;
        }, [is_agency]);

        const userEditProfileUrl = useMemo(() => {
            if (is_agency) {
                return (
                    routeConstants.profile.editProfile.agency.root +
                    routeConstants.profile.editProfile.agency.companyDetails
                );
            } else
                return (
                    routeConstants.profile.editProfile.root +
                    routeConstants.profile.editProfile.personalDetails
                );
        }, [is_agency]);

        const profileLinkName = useMemo(() => {
            if (pathname === userProfileUrl) {
                return "Edit Profile";
            } else return "Profile";
        }, [pathname, userProfileUrl]);

        const logOut = useCallback(() => {
            setMenuOpen(false);
            const userAuth = localStorage.getItem("userAuth");
            logout((userAuth as unknown as AuthState)?.access_token);
        }, [access_token, logout, setMenuOpen]);

        const userNameInitials = useMemo(
            () => getNameInitials(`${first_name} ${last_name}`),
            [first_name, last_name]
        );

        const handleProfileNavigation = useCallback(() => {
            setMenuOpen(false);
            if (pathname === userProfileUrl) {
                navigateTo(userEditProfileUrl);
            } else {
                navigateTo(userProfileUrl);
            }
        }, [
            navigateTo,
            pathname,
            setMenuOpen,
            userEditProfileUrl,
            userProfileUrl,
        ]);

        useEffect(() => {
            if (data) {
                dispatch(logoutUser());
                dispatch(clearUserState());
            }
        }, [data, dispatch]);

        return (
            <Styled.Menu>
                <div>
                    <Styled.HamburgerButton
                        onClick={toggleMenu}
                        menuOpen={menuOpen}
                        ref={buttonRef}
                        aria-label="Menu"
                    >
                        <Styled.HamburgerButtonBox>
                            <div className="ham-box-inner" />
                        </Styled.HamburgerButtonBox>
                    </Styled.HamburgerButton>

                    <Styled.Sidebar
                        menuOpen={menuOpen}
                        aria-hidden={!menuOpen}
                        tabIndex={menuOpen ? 1 : -1}
                    >
                        <Styled.SidebarInnerWrapper>
                            <Styled.SidebarNav ref={navRef}>
                                <Styled.SidebarNavList>
                                    <Styled.SidebarNavItem
                                        onClick={handleProfileNavigation}
                                    >
                                        <Styled.SidebarNavLink isActive={false}>
                                            {profileLinkName}
                                        </Styled.SidebarNavLink>
                                        <IoIosArrowForward
                                            size={18}
                                            id="right-arrow"
                                        />
                                    </Styled.SidebarNavItem>
                                    <Styled.SidebarNavItem
                                        onClick={() => {
                                            setMenuOpen(false);
                                            navigateTo("/find-work");
                                        }}
                                    >
                                        <Styled.SidebarNavLink
                                            isActive={pathname === "/find-work"}
                                        >
                                            Find Work
                                        </Styled.SidebarNavLink>
                                        <IoIosArrowForward
                                            size={18}
                                            id="right-arrow"
                                        />
                                    </Styled.SidebarNavItem>
                                    <Styled.SidebarNavItem
                                        onClick={() => {
                                            setMenuOpen(false);
                                            navigateTo("/find-talent");
                                        }}
                                    >
                                        <Styled.SidebarNavLink
                                            isActive={
                                                pathname === "/find-talent"
                                            }
                                        >
                                            Find Talent
                                        </Styled.SidebarNavLink>
                                        <IoIosArrowForward
                                            size={18}
                                            id="right-arrow"
                                        />
                                    </Styled.SidebarNavItem>
                                </Styled.SidebarNavList>
                            </Styled.SidebarNav>
                            <Styled.SidebarNavItem>
                                <Styled.SidebarProfileItem
                                    onClick={() => {
                                        setMenuOpen(false);
                                        navigateTo(routeConstants.profile.root);
                                    }}
                                >
                                    <Styled.AppAvatar size="S">
                                        {images?.profile?.url ? (
                                            <img
                                                src={images?.profile?.url}
                                                onError={handleError}
                                                alt="user profile"
                                            />
                                        ) : (
                                            <>{userNameInitials}</>
                                        )}
                                    </Styled.AppAvatar>
                                    {`${first_name} ${last_name}`}
                                </Styled.SidebarProfileItem>
                                <Styled.LogoutButton onClick={logOut}>
                                    <FiLogOut size={18} />
                                    Logout
                                </Styled.LogoutButton>
                            </Styled.SidebarNavItem>
                        </Styled.SidebarInnerWrapper>
                    </Styled.Sidebar>
                </div>
            </Styled.Menu>
        );
    }
);

Menu.displayName = "Menu";
